import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Stack,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Chat as ChatIcon,
  CheckCircle,
  Cancel,
  RadioButtonUnchecked,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  ExpandMore as ExpandMoreIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import axios from '../../../api/axios';
import DomeGallery from '../../../components/Dashboard/Student/DomeGallery';
import SubmissionHistoryDialog from '../../../components/Dashboard/Student/SubmissionHistoryDialog';

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const daysAgo = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (daysAgo === 0) return '今天';
  if (daysAgo === 1) return '昨天';
  if (daysAgo < 7) return `${daysAgo}天前`;
  return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' });
};

function NotebookMemoryTab({ notebookId, selectedCourse }) {
  const navigate = useNavigate();
  const [notebookExampleIds, setNotebookExampleIds] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [submissionHistoryDialogOpen, setSubmissionHistoryDialogOpen] = useState(false);
  const [selectedQuestionSubmissions, setSelectedQuestionSubmissions] = useState([]);
  const [selectedQuestionInfo, setSelectedQuestionInfo] = useState(null);

  useEffect(() => {
    if (!notebookId) return;
    const fetchNotebookExamples = async () => {
      try {
        const res = await axios.get(`/notebook-examples/notebook/${encodeURIComponent(notebookId)}`);
        const ids = (res.data?.examples || []).map((e) => e.id);
        setNotebookExampleIds(ids);
      } catch (err) {
        console.warn('Failed to fetch notebook examples for filter:', err);
        setNotebookExampleIds([]);
      }
    };
    fetchNotebookExamples();
  }, [notebookId]);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        setError(null);
        const params = {};
        if (selectedCourse?.id) {
          params.courseTemplateId = selectedCourse.id;
        }
        const response = await axios.get('/notebook-examples/chat/conversations', { params });
        setConversations(response.data || []);
      } catch (err) {
        console.error('Error fetching conversations:', err);
        setError(err.response?.data?.error || err.message || '获取对话记录失败');
      } finally {
        setLoading(false);
      }
    };
    fetchConversations();
  }, [notebookId, selectedCourse?.id]);

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        setLoadingSubmissions(true);
        const params = {};
        if (selectedCourse?.id) {
          params.courseTemplateId = selectedCourse.id;
        }
        const response = await axios.get('/submissions/my-submissions', { params });
        setSubmissions(response.data || []);
      } catch (err) {
        console.error('Error fetching submissions:', err);
      } finally {
        setLoadingSubmissions(false);
      }
    };
    fetchSubmissions();
  }, [selectedCourse?.id]);

  const displayedConversations = useMemo(() => {
    if (!notebookId || !notebookExampleIds.length) return [];
    const idSet = new Set(notebookExampleIds);
    return conversations.filter((c) => {
      const qId = c.questionId?.toString();
      if (!qId || !qId.startsWith('notebook_')) return false;
      const exId = parseInt(qId.replace('notebook_', ''), 10);
      return idSet.has(exId);
    });
  }, [conversations, notebookId, notebookExampleIds]);

  const groupedSubmissions = useMemo(() => {
    const groupedMap = new Map();
    submissions.forEach((sub) => {
      let questionId =
        typeof sub.questionId === 'object' && sub.questionId !== null
          ? sub.questionId._id || sub.questionId
          : sub.questionId;
      const questionIdStr = questionId?.toString() || questionId;
      if (!groupedMap.has(questionIdStr)) {
        groupedMap.set(questionIdStr, {
          questionId: questionIdStr,
          questionInfo:
            typeof sub.questionId === 'object' && sub.questionId !== null
              ? sub.questionId
              : {
                  _id: questionId,
                  title: sub.questionTitle,
                  category: sub.category,
                  questionNumber: sub.questionNumber,
                },
          submissions: [],
        });
      }
      groupedMap.get(questionIdStr).submissions.push(sub);
    });
    groupedMap.forEach((group) => {
      group.submissions.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
    });
    return Array.from(groupedMap.values()).sort((a, b) => {
      const latestA = a.submissions[0]?.submittedAt;
      const latestB = b.submissions[0]?.submittedAt;
      return new Date(latestB) - new Date(latestA);
    });
  }, [submissions]);

  const displayedGroupedSubmissions = useMemo(() => {
    if (!notebookId || !notebookExampleIds.length) return [];
    const idSet = new Set(notebookExampleIds);
    return groupedSubmissions.filter((group) => {
      const qId = group.questionId?.toString();
      if (!qId || !qId.startsWith('notebook_')) return false;
      const exId = parseInt(qId.replace('notebook_', ''), 10);
      return idSet.has(exId);
    });
  }, [groupedSubmissions, notebookId, notebookExampleIds]);

  const getStatusIcon = (status) => {
    if (!status) return <RadioButtonUnchecked color="disabled" />;
    if (status === 'passed') return <CheckCircle color="success" />;
    return <Cancel color="error" />;
  };

  const allRecords = useMemo(() => {
    const records = [];
    displayedConversations.forEach((conv) => {
      records.push({
        type: 'conversation',
        id: conv.id,
        title: conv.questionTitle || '未命名题目',
        questionType: conv.questionType,
        category: conv.category || '其他',
        preview: conv.preview,
        time: conv.lastMessageAt,
        meta: `${conv.messageCount || 0} 条消息`,
        data: conv,
      });
    });
    displayedGroupedSubmissions.forEach((group) => {
      const sub = group.submissions[0];
      records.push({
        type: 'submission',
        id: sub.id,
        title: group.questionInfo.title || '未命名题目',
        questionType: null,
        category: group.questionInfo.category || '其他',
        questionNumber: group.questionInfo.questionNumber,
        status: sub.status,
        score: sub.score,
        time: sub.submittedAt,
        meta: `分数: ${sub.score} • ${group.submissions.length} 次提交`,
        data: group,
      });
    });
    return records.sort((a, b) => {
      const timeA = a.time ? new Date(a.time).getTime() : 0;
      const timeB = b.time ? new Date(b.time).getTime() : 0;
      return timeB - timeA;
    });
  }, [displayedConversations, displayedGroupedSubmissions]);

  const recordsByCategory = useMemo(() => {
    const categoryMap = new Map();
    allRecords.forEach((record) => {
      const category = record.category || '其他';
      if (!categoryMap.has(category)) categoryMap.set(category, []);
      categoryMap.get(category).push(record);
    });
    return Array.from(categoryMap.entries())
      .map(([category, records]) => ({
        category,
        records: records.sort((a, b) => {
          const timeA = a.time ? new Date(a.time).getTime() : 0;
          const timeB = b.time ? new Date(b.time).getTime() : 0;
          return timeB - timeA;
        }),
      }))
      .sort((a, b) => {
        if (a.category === '其他') return 1;
        if (b.category === '其他') return -1;
        return a.category.localeCompare(b.category, 'zh-CN');
      });
  }, [allRecords]);

  const conversationImages = displayedConversations.map((conv) => {
    const imageSeed = conv.id || Math.random();
    const imageSize = 300 + (conv.messageCount || 0) * 10;
    const imageUrl = `https://picsum.photos/seed/${encodeURIComponent(conv.questionTitle || imageSeed)}/${imageSize}/${imageSize}?grayscale`;
    const preview = conv.preview || '点击查看对话';
    const truncatedPreview = preview.length > 50 ? preview.substring(0, 50) + '...' : preview;
    return {
      src: imageUrl,
      image: imageUrl,
      alt: conv.questionTitle || '未命名题目',
      title: conv.questionTitle || '未命名题目',
      link: `/memory/${conv.id}`,
      description: truncatedPreview,
      meta: `${formatDate(conv.lastMessageAt)} • ${conv.messageCount || 0} 条消息`,
      type: 'conversation',
      data: conv,
    };
  });

  const submissionImages = displayedGroupedSubmissions.map((group) => {
    const latestSub = group.submissions[0];
    const sub = latestSub;
    const questionTitle = group.questionInfo.title || '未命名题目';
    const questionIdStr = group.questionId?.toString() || group.questionId;
    const imageSize = 300 + (sub.score || 0) * 2;
    const imageUrl = `https://picsum.photos/seed/${encodeURIComponent(questionTitle + sub.id)}/${imageSize}/${imageSize}?grayscale`;
    const link =
      questionIdStr && questionIdStr.startsWith('notebook_')
        ? `/notebook-exercise/${questionIdStr.replace('notebook_', '')}`
        : `/question/${questionIdStr}`;
    return {
      src: imageUrl,
      image: imageUrl,
      alt: questionTitle,
      title: questionTitle,
      link,
      description: `分数: ${sub.score} • ${sub.status === 'passed' ? '通过' : sub.status === 'failed' ? '未通过' : '待评分'}`,
      meta: `${formatDate(sub.submittedAt)} • ${group.submissions.length} 次提交`,
      type: 'submission',
      data: group,
    };
  });

  const galleryImages = [...conversationImages, ...submissionImages].sort((a, b) => {
    const timeA = a.type === 'conversation' ? a.data.lastMessageAt : a.data.submittedAt;
    const timeB = b.type === 'conversation' ? b.data.lastMessageAt : b.data.submittedAt;
    return (timeB ? new Date(timeB).getTime() : 0) - (timeA ? new Date(timeA).getTime() : 0);
  });

  const handleConversationClick = (conv) => {
    navigate(`/memory/${conv.id}`);
  };

  const handleViewSubmissionHistory = (group) => {
    setSelectedQuestionInfo(group.questionInfo);
    setSelectedQuestionSubmissions(group.submissions);
    setSubmissionHistoryDialogOpen(true);
  };

  const handleCloseSubmissionHistory = () => {
    setSubmissionHistoryDialogOpen(false);
    setSelectedQuestionSubmissions([]);
    setSelectedQuestionInfo(null);
  };

  const handleItemClick = (item) => {
    const record = galleryImages.find((img) => img.src === item.src || img.image === item.src);
    if (record) {
      if (record.type === 'conversation') {
        handleConversationClick(record.data);
      } else {
        handleViewSubmissionHistory(record.data);
      }
    } else if (item.link) {
      navigate(item.link);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error" sx={{ mb: 2 }}>
          加载失败: {error}
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="subtitle1" color="text.secondary">
          本笔记本下的 AI 解答对话与做题提交记录
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => {
            if (newMode !== null) setViewMode(newMode);
          }}
          size="small"
        >
          <ToggleButton value="gallery" aria-label="画廊视图">
            <GridViewIcon sx={{ mr: 0.5 }} />
            画廊
          </ToggleButton>
          <ToggleButton value="list" aria-label="列表视图">
            <ListViewIcon sx={{ mr: 0.5 }} />
            列表
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {loadingSubmissions && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {allRecords.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <ChatIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
            本笔记下暂无对话与做题记录
          </Typography>
          <Typography variant="body2" color="text.secondary">
            在本笔记题目下与 AI 对话或提交答案后，记录会显示在这里
          </Typography>
        </Paper>
      ) : viewMode === 'gallery' ? (
        <Box sx={{ minHeight: 400, position: 'relative', width: '100%' }}>
          <DomeGallery
            images={galleryImages}
            fit={0.4}
            minRadius={500}
            maxVerticalRotationDeg={5}
            segments={20}
            dragDampening={2}
            grayscale={true}
            onItemClick={handleItemClick}
          />
        </Box>
      ) : (
        <Box>
          {recordsByCategory.map(({ category, records }) => (
            <Accordion key={category} defaultExpanded={recordsByCategory.length <= 3} sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                  <CategoryIcon color="primary" />
                  <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                    {category}
                  </Typography>
                  <Chip label={`${records.length} 条`} size="small" color="primary" variant="outlined" />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {records.map((record) => (
                    <Grid item xs={12} sm={6} md={4} key={`${record.type}-${record.id}`}>
                      <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardActionArea
                          onClick={() => {
                            if (record.type === 'conversation') {
                              handleConversationClick(record.data);
                            } else {
                              handleViewSubmissionHistory(record.data);
                            }
                          }}
                          sx={{ height: '100%' }}
                        >
                          <CardContent>
                            <Stack spacing={1}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {record.type === 'conversation' ? (
                                  <ChatIcon fontSize="small" color="primary" />
                                ) : (
                                  getStatusIcon(record.status)
                                )}
                                <Chip
                                  label={record.type === 'conversation' ? '对话' : '做题'}
                                  size="small"
                                  variant="outlined"
                                />
                              </Stack>
                              <Typography variant="subtitle2" fontWeight="600" noWrap>
                                {record.title}
                              </Typography>
                              {record.type === 'conversation' && record.preview && (
                                <Typography variant="body2" color="text.secondary" sx={{ WebkitLineClamp: 2, display: '-webkit-box', overflow: 'hidden' }}>
                                  {record.preview}
                                </Typography>
                              )}
                              {record.type === 'submission' && (
                                <Chip
                                  label={record.status === 'passed' ? '通过' : record.status === 'failed' ? '未通过' : '待评分'}
                                  size="small"
                                  color={record.status === 'passed' ? 'success' : record.status === 'failed' ? 'error' : 'warning'}
                                  icon={getStatusIcon(record.status)}
                                />
                              )}
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(record.time)} · {record.meta}
                              </Typography>
                            </Stack>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      <SubmissionHistoryDialog
        open={submissionHistoryDialogOpen}
        onClose={handleCloseSubmissionHistory}
        questionInfo={selectedQuestionInfo}
        submissions={selectedQuestionSubmissions}
      />
    </Box>
  );
}

export default NotebookMemoryTab;
